import type { UrlHostsObj } from "@v2/features/analytics/hooks/useUrlHosts";
import type {
  InternalQuery,
  UrlSyncedQuery,
} from "@v2/features/analytics/utils/query";
import type { DateRange } from "react-day-picker";
import type {
  AnalyticsQuery,
  ConditionOperator,
  MetricName,
  PageType,
} from "schemas/generated/analyticsRead";
import type {
  ChartInterval,
  ComparisonTimeFrame,
  RelativeTimeFrame,
} from "schemas/utils/analytics";

import * as React from "react";

import {
  DEFAULT_FILTERS,
  DEFAULT_QUERY,
} from "@v2/features/analytics/constants";
import {
  DEFAULT_CHART_INTERVAL,
  DEFAULT_COMPARE_RANGE_TIME_FRAME,
  DEFAULT_MAIN_RELATIVE_TIME_FRAME,
} from "analytics-utils/analytics";

type UrlParamPayload = {
  attribute: string;
  operator: ConditionOperator;
  value: string;
};

type BaseAnalyticsQueryAction =
  | {
      type: "updatePagination";
      payload: AnalyticsQuery["offset"];
    }
  | {
      type: "filters.replaceUrlPath";
      payload: string;
    }
  | {
      type: "sortMetric";
      payload: {
        sortKey: MetricName;
        sortOrder: AnalyticsQuery["order"];
      };
    }
  | {
      type: "urlHosts";
      payload: AnalyticsQuery["urlHosts"];
    }
  | {
      type: "updateMainRange";
      payload: RelativeTimeFrame | DateRange;
    }
  | {
      type: "updateCompareRange";
      payload: ComparisonTimeFrame | DateRange;
    }
  | {
      type: "filters.updateUrlPathSearch";
      payload: string;
    }
  | {
      type: "updateChartInterval";
      payload: ChartInterval;
    }
  | {
      type: "initDeepDivePage";
      payload: {
        urlPath: string;
      };
    }
  | {
      type: "initOverviewPage";
    }
  | {
      type: "filters.deleteUrlParam";
      payload: UrlParamPayload;
    }
  | {
      type: "filters.addUrlParam";
      payload: UrlParamPayload;
    }
  | {
      type: "openDeepDivePageFromOverviewPage";
      payload: {
        urlPath: string;
        source: "analytics_overview" | "analytics_pages";
      };
    }
  | {
      type: "toggleFractional";
      payload: boolean;
    }
  | {
      type: "updatePageTypeAndFractional";
      payload: {
        pageType: PageType;
        isFractional: boolean;
      };
    };

export type AnalyticsQueryAction =
  | BaseAnalyticsQueryAction
  | {
      type: "filters.pageType";
      payload: PageType;
    };

const DEFAULT_URL_SYNCED_QUERY: UrlSyncedQuery = {
  selectedTimePeriod: {
    type: "relative",
    value: DEFAULT_MAIN_RELATIVE_TIME_FRAME,
  },
  selectedComparePeriod: {
    type: "relative",
    value: DEFAULT_COMPARE_RANGE_TIME_FRAME,
  },
  chartInterval: DEFAULT_CHART_INTERVAL,
  filters: DEFAULT_FILTERS,
  urlHosts: [],
  mode: "groupByUrlPath",
  isFractional: true,
};

export const AnalyticsQueryContext = React.createContext<{
  resetInternalAndUrlSyncedQueries: () => void;
  dispatchAnalyticsQuery: (action: AnalyticsQueryAction) => void;
  query: UrlSyncedQuery & InternalQuery;
  urlHostsObj: UrlHostsObj;
  workspaceId: string | undefined;
  projectId: string | undefined;
}>({
  resetInternalAndUrlSyncedQueries: () => null,
  urlHostsObj: {
    data: [],
    isLoading: false,
  },
  dispatchAnalyticsQuery: () => null,
  query: { ...DEFAULT_QUERY, ...DEFAULT_URL_SYNCED_QUERY },
  workspaceId: undefined,
  projectId: undefined,
});

export const useAnalyticsQueryContext = () => {
  return React.useContext(AnalyticsQueryContext);
};
